import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ProductTable = ({ 
  products, 
  selectedProducts, 
  onSelectProduct, 
  onSelectAll, 
  onSort, 
  sortConfig,
  onViewDetails,
  onEditProduct
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStockStatusColor = (stock, reorderPoint) => {
    if (stock === 0) return 'text-error bg-error/10';
    if (stock <= reorderPoint) return 'text-warning bg-warning/10';
    return 'text-success bg-success/10';
  };

  const getStockStatusText = (stock, reorderPoint) => {
    if (stock === 0) return 'Out of Stock';
    if (stock <= reorderPoint) return 'Low Stock';
    return 'In Stock';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (value) => {
    if (!value) return '—';

    // Firestore Timestamp with toDate()
    if (value && typeof value.toDate === 'function') {
      const d = value.toDate();
      return formatShortDate(d);
    }

    // Firestore Timestamp-like object { seconds, nanoseconds }
    if (value && typeof value.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return formatShortDate(d);
    }

    // Firestore Admin Timestamp serialized as {_seconds, _nanoseconds}
    if (value && typeof value._seconds === 'number') {
      const d = new Date(value._seconds * 1000);
      return formatShortDate(d);
    }

    // Milliseconds since epoch
    if (typeof value === 'number') {
      const d = new Date(value);
      return !isNaN(d) ? formatShortDate(d) : '—';
    }

    // ISO string or Date
    if (typeof value === 'string' || value instanceof Date) {
      const d = new Date(value);
      if (!isNaN(d)) return formatShortDate(d);

      // Unparseable string (e.g., "26 October 2025 at 01:53:16 UTC+1")
      return value;
    }

    // Fallback: try to construct
    try {
      const d = new Date(value);
      return !isNaN(d) ? formatShortDate(d) : '—';
    } catch {
      return '—';
    }
  };

  const formatShortDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const handleSort = (column) => {
    onSort(column);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedProducts?.length === products?.length && products?.length > 0}
                  indeterminate={selectedProducts?.length > 0 && selectedProducts?.length < products?.length}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              {[
                { key: 'name', label: 'Product Name' },
                { key: 'sku', label: 'SKU' },
                { key: 'category', label: 'Category' },
                { key: 'stock', label: 'Stock' },
                { key: 'price', label: 'Price' },
                { key: 'lastUpdated', label: 'Last Updated' }
              ]?.map((column) => (
                <th 
                  key={column?.key}
                  className="px-4 py-3 text-left text-sm font-medium text-foreground cursor-pointer hover:bg-muted/70 transition-colors"
                  onClick={() => handleSort(column?.key)}
                >
                  <div className="flex items-center space-x-2">
                    <span>{column?.label}</span>
                    <Icon 
                      name={getSortIcon(column?.key)} 
                      size={14} 
                      className="text-muted-foreground" 
                    />
                  </div>
                </th>
              ))}
              <th className="w-32 px-4 py-3 text-center text-sm font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products?.map((product) => (
              <tr 
                key={product?.id}
                className="hover:bg-muted/30 transition-colors"
                onMouseEnter={() => setHoveredRow(product?.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedProducts?.includes(product?.id)}
                    onChange={(e) => onSelectProduct(product?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="Package" size={20} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{product?.name}</p>
                      <p className="text-sm text-muted-foreground">{product?.description}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="font-data text-sm text-foreground">{product?.sku}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary">
                    {product?.category}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">{product?.stock}</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStockStatusColor(product?.stock, product?.reorderPoint)}`}>
                      {getStockStatusText(product?.stock, product?.reorderPoint)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="font-medium text-foreground">{formatCurrency(product?.price)}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">{formatDate(product?.lastUpdated)}</span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewDetails(product?.id)}
                      title="View Details"
                      className="h-8 w-8"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditProduct(product?.id)}
                      title="Edit Product"
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3 sm:space-y-4 p-3 sm:p-4">
        {products?.map((product) => (
          <div key={product?.id} className="bg-card border border-border rounded-lg p-3 sm:p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Checkbox
                  checked={selectedProducts?.includes(product?.id)}
                  onChange={(e) => onSelectProduct(product?.id, e?.target?.checked)}
                />
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name="Package" size={20} className="text-muted-foreground sm:hidden" />
                  <Icon name="Package" size={24} className="text-muted-foreground hidden sm:block" />
                </div>
                <div className="flex-1 min-w-0"> {/* min-width prevents text overflow */}
                  <h3 className="font-medium text-foreground truncate text-sm sm:text-base">{product?.name}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground font-data truncate">{product?.sku}</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Category</p>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary truncate max-w-full">
                  {product?.category}
                </span>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">Price</p>
                <span className="font-medium text-foreground text-sm sm:text-base">{formatCurrency(product?.price)}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStockStatusColor(product?.stock, product?.reorderPoint)}`}>
                  {getStockStatusText(product?.stock, product?.reorderPoint)}
                </span>
                <span className="text-xs text-muted-foreground">Updated {formatDate(product?.lastUpdated)}</span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewDetails(product?.id)}
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={16}
                  className="flex-1 sm:flex-none px-2 sm:px-3"
                >
                  <span className="hidden xs:inline">View</span>
                  <span className="xs:hidden">View</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditProduct(product?.id)}
                  iconName="Edit"
                  iconPosition="left"
                  iconSize={16}
                  className="flex-1 sm:flex-none px-2 sm:px-3"
                >
                  <span className="hidden xs:inline">Edit</span>
                  <span className="xs:hidden">Edit</span>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {products?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No products found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or add a new product to get started.</p>
        </div>
      )}
    </div>
  );
};

export default ProductTable;